export type ParseWorkflowType = 'cv-parse' | 'packet-parse' | 'insurance-parse';

/**
 * The extensions each parse workflow reads (MCV-1760). A document the vault parses (a CV, a
 * packet, a malpractice insurance certificate) may only be one of these (MCV-1802); every other
 * type takes any storable format. Mirrors PARSE_EXTENSIONS_BY_WORKFLOW in
 * apps/mcv-api/mcv_api/routes/document/constants.py; fileTypes.parity.spec.ts fails on drift.
 */
export const PARSE_EXTENSIONS_BY_WORKFLOW: Record<ParseWorkflowType, readonly string[]> = {
  'cv-parse': ['pdf', 'docx', 'doc', 'txt', 'html', 'md'],
  'packet-parse': ['pdf', 'docx', 'doc', 'png', 'jpg', 'jpeg'],
  'insurance-parse': ['pdf'],
};

// A name with no dot has no extension (a file literally named "pdf" is not a PDF).
export const getFileExtension = (fileName: string): string => {
  const dot = fileName.lastIndexOf('.');
  return dot === -1 ? '' : fileName.slice(dot + 1);
};

/** Whether the parse workflow reads this file, judged by its extension. */
export const canParseFile = (workflow: ParseWorkflowType, fileName: string): boolean =>
  PARSE_EXTENSIONS_BY_WORKFLOW[workflow].includes(getFileExtension(fileName).toLowerCase());

/** The category and type the API promotes to an `insurance-parse` job on upload. */
export const INSURANCE_CERTIFICATE_UPLOAD = {
  docCategory: 'malpractice_insurance',
  docType: 'certificate',
} as const;

interface ParseableDocument {
  docType: string;
  docCategory: string;
}

/**
 * The parse workflow a document is parsed with, or null when it cannot be AI parsed.
 * The manual re-parse (PATCH /documents/parse/{id}) must send it: the API defaults to `cv-parse`.
 */
export const resolveParseWorkflow = ({
  docType,
  docCategory,
}: ParseableDocument): ParseWorkflowType | null => {
  if (docType === 'cv') return 'cv-parse';
  if (docType === 'packet') return 'packet-parse';
  if (
    docType === INSURANCE_CERTIFICATE_UPLOAD.docType &&
    docCategory === INSURANCE_CERTIFICATE_UPLOAD.docCategory
  ) {
    return 'insurance-parse';
  }
  return null;
};

/** Whether the grid offers Parse: the type leads to a workflow AND that workflow reads the file. */
export const canAiParseDocument = (
  doc: (ParseableDocument & { docName: string }) | undefined | null
): boolean => {
  if (!doc) return false;
  const workflow = resolveParseWorkflow(doc);
  return workflow !== null && canParseFile(workflow, doc.docName);
};
